import { useState, useRef, useCallback } from 'react'
import { MapPin, Phone, Clock, ChevronLeft, ChevronRight, ExternalLink, Navigation } from 'lucide-react'
import {
  MAGASINS,
  CARTE_HAMDALLAYE,
  NOMBRE_DE_MAGASINS,
  NOMBRE_DE_VILLES,
  VILLES,
  magasinsDeLaVille,
} from '../data/magasins'
import './Stores.css'
import { lienTel } from '../data/contact'

/**
 * La liste vit dans src/data/magasins.js.
 *
 * Elle etait ecrite ici, et l'accueil annoncait de son cote « 10+ magasins »
 * pendant qu'elle en comptait neuf. Un chiffre recopie ne suit pas
 * l'ouverture d'une boutique ; celui-ci est desormais derive.
 *
 * Les notes et nombres d'avis par magasin ont disparu avec le deplacement.
 * Ils etaient inventes : « 4.9 (312 avis) » a Hamdallaye, « 4.7 (88) » a
 * Kankan, et ainsi de suite pour les neuf. Aucun de ces avis n'existe, ni
 * chez nous ni sur Google, et une note affichee sur une facade de magasin
 * est exactement le genre de detail qu'un client verifie.
 */
const STORES = MAGASINS

const CITIES = ['Tous', ...VILLES]

const texts = {
  fr: {
    tag: 'Nos Magasins',
    title: [`${NOMBRE_DE_MAGASINS} magasins à votre`, 'service en Guinée'],
    subtitle: 'Du mobilier haut de gamme disponible près de chez vous. Trouvez le magasin ABBUSINESS le plus proche.',
    map: 'Voir sur la carte',
    cities: { Tous: 'Tous', Conakry: 'Conakry', Labé: 'Labé', Kankan: 'Kankan' },
    closed: 'Fermé le dimanche',
  },
  en: {
    tag: 'Our Stores',
    title: [`${NOMBRE_DE_MAGASINS} stores at your`, 'service in Guinea'],
    subtitle: 'Premium furniture available close to you. Find the nearest ABBUSINESS store.',
    map: 'View on map',
    cities: { Tous: 'All', Conakry: 'Conakry', Labé: 'Labé', Kankan: 'Kankan' },
    closed: 'Closed on Sundays',
  },
}

function PatternCircles() {
  return (
    <svg className="store-pattern" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice">
      {[...Array(6)].map((_, i) => (
        <circle key={i} cx={30 + i * 35} cy={i % 2 === 0 ? 60 : 140} r={28} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2"/>
      ))}
      <circle cx="170" cy="60" r="48" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="2"/>
    </svg>
  )
}
function PatternDots() {
  return (
    <svg className="store-pattern" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice">
      {[...Array(5)].map((_, r) =>
        [...Array(5)].map((_, c) => (
          <circle key={`${r}-${c}`} cx={20 + c * 40} cy={20 + r * 40} r={2.5} fill="rgba(255,255,255,0.12)"/>
        ))
      )}
    </svg>
  )
}
function PatternGrid() {
  return (
    <svg className="store-pattern" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice">
      {[0,50,100,150,200].map(x => <line key={`v${x}`} x1={x} y1={0} x2={x} y2={200} stroke="rgba(255,255,255,0.07)" strokeWidth="1"/>)}
      {[0,50,100,150,200].map(y => <line key={`h${y}`} x1={0} y1={y} x2={200} y2={y} stroke="rgba(255,255,255,0.07)" strokeWidth="1"/>)}
    </svg>
  )
}

const PATTERNS = { circles: PatternCircles, dots: PatternDots, grid: PatternGrid, diamonds: PatternGrid, waves: PatternCircles, hexagons: PatternDots }

export default function Stores({ lang }) {
  const t = texts[lang]
  const [activeCity, setActiveCity] = useState('Tous')
  const [currentIndex, setCurrentIndex] = useState(0)

  const filtered = magasinsDeLaVille(activeCity)

  const CARDS_VISIBLE = 3
  const maxIndex = Math.max(0, filtered.length - CARDS_VISIBLE)

  const handleCityChange = (city) => { setActiveCity(city); setCurrentIndex(0) }

  const prev = useCallback(() => setCurrentIndex(i => Math.max(0, i - 1)), [])
  const next = useCallback(() => setCurrentIndex(i => Math.min(maxIndex, i + 1)), [maxIndex])

  const cityCount = (city) => magasinsDeLaVille(city).length

  return (
    <section id="stores" className="stores">
      <div className="stores-bg-orb stores-orb-1" />
      <div className="stores-bg-orb stores-orb-2" />

      <div className="stores-inner">
        <div className="stores-header reveal">
          <span className="section-tag">{t.tag}</span>
          <h2 className="section-title">{t.title[0]}<br /><span>{t.title[1]}</span></h2>
          <p className="section-subtitle">{t.subtitle}</p>
        </div>

        <div className="stores-filters reveal">
          {CITIES.map(city => (
            <button key={city} className={`store-filter-btn ${activeCity === city ? 'active' : ''}`} onClick={() => handleCityChange(city)}>
              {t.cities[city]}
              <span className="filter-count">{cityCount(city)}</span>
            </button>
          ))}
        </div>

        <div className="stores-carousel-wrap reveal">
          <button className={`carousel-nav carousel-prev ${currentIndex === 0 ? 'disabled' : ''}`} onClick={prev} aria-label="Précédent">
            <ChevronLeft size={20} />
          </button>

          <div className="carousel-viewport">
            <div
              className="carousel-track"
              style={{ transform: `translateX(calc(-${currentIndex * (100 / CARDS_VISIBLE)}% - ${currentIndex * 8}px))` }}
            >
              {filtered.map((store) => {
                const PatternComp = PATTERNS[store.pattern]
                return (
                  <article key={store.id} className="store-card">
                    <div className="store-card-top" style={{ background: store.gradient }}>
                      <PatternComp />
                      <div className="store-number">#{store.id}</div>
                      <div className="store-city-tag"><MapPin size={10} />{store.city}</div>
                      <div className="store-icon-wrap">
                        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="36" height="36">
                          <path d="M8 20L24 6L40 20V42H30V30H18V42H8V20Z" fill="rgba(255,255,255,0.9)"/>
                        </svg>
                      </div>
                    </div>

                    <div className="store-card-body">
                      <div>
                        <h3 className="store-name">{store.name}</h3>
                        <div className="store-zone">{store.zone}</div>
                      </div>

                      <div className="store-details">
                        <div className="store-detail-row">
                          <MapPin size={13} className="detail-icon" />
                          <span>{store.address}</span>
                        </div>
                        <div className="store-detail-row">
                          <Phone size={13} className="detail-icon" />
                          <a href={lienTel(`+224 ${store.phone}`)}>{store.phone}</a>
                        </div>
                        <div className="store-detail-row">
                          <Clock size={13} className="detail-icon" />
                          <span>{store.hours}</span>
                        </div>
                      </div>

                      <a href={CARTE_HAMDALLAYE} target="_blank" rel="noopener noreferrer" className="store-map-btn">
                        <Navigation size={13} />
                        {t.map}
                        <ExternalLink size={11} className="map-ext" />
                      </a>
                    </div>
                  </article>
                )
              })}
            </div>
          </div>

          <button className={`carousel-nav carousel-next ${currentIndex >= maxIndex ? 'disabled' : ''}`} onClick={next} aria-label="Suivant">
            <ChevronRight size={20} />
          </button>
        </div>

        {filtered.length > CARDS_VISIBLE && (
          <div className="carousel-dots">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button key={i} className={`carousel-dot ${i === currentIndex ? 'active' : ''}`} onClick={() => setCurrentIndex(i)} />
            ))}
          </div>
        )}

        {/*
          | Trois chiffres, tous verifiables. Le quatrieme etait « 4.8★ note
          | moyenne » : il n'y a aucun avis en base pour le calculer, et il
          | ne venait d'aucune source exterieure non plus.
        */}
        <div className="stores-stats reveal">
          <div className="stores-stat"><span className="stores-stat-value">{NOMBRE_DE_MAGASINS}</span><span className="stores-stat-label">Magasins</span></div>
          <div className="stores-stat-divider" />
          <div className="stores-stat"><span className="stores-stat-value">{NOMBRE_DE_VILLES}</span><span className="stores-stat-label">Villes</span></div>
          <div className="stores-stat-divider" />
          <div className="stores-stat"><span className="stores-stat-value">9h–18h</span><span className="stores-stat-label">Lun – Sam</span></div>
        </div>
      </div>
    </section>
  )
}
